import { t } from 'i18next';
import Menu, { Divider, MenuItem, SubMenu } from 'rc-menu';
import { FC } from 'react';
import { GroupTypes } from '../../../../../../shared/common/group';
import { MainNavLoginButton, MainNavSignupButton } from '../../../../../../shared/frontend/components/mainNav/buttons';
import { handleRCMenuOnClick } from '../../../../../../shared/frontend/libs/history';
import { LevelCap } from '../../../../shared/enums';
import type { TClaimedCharacter } from '../../../../../../shared/types/characterTypes';
import { withSiteMeta } from '../../../../../../shared/frontend/HOCS/withSiteMeta';
import { namedRoutes, resolveRoutePath } from '../../bundles/routes';
import { MessageCenterIcon } from '../presentational/messageCenterIcon';
import { SpecIcon } from '../../../../../../shared/frontend/scripts/wow/components/SpecIcon';
import { NotificationBell } from '../../../../../../shared/frontend/components/mainNav/NotificationBell';
import { UserAvatarIcon } from '../../../../../../shared/frontend/components/presentational/userAvatarIcon';
import { DropdownTitle } from '../../../../../../shared/frontend/components/menu/dropdownTitle';
import { FeatureFlag } from '../../../../../../shared/gameConfig/sharedTypes';
import { isTalentFeedbackAdminEnabled } from '../../talents/feedback/talentFeedbackAccess';

function sortTeams(a: any, b: any) {
  const aIsActive = a.status === 'active';
  const bIsActive = b.status === 'active';
  if (aIsActive === bIsActive) {
    if (a.slug === b.slug) {
      return a.region.slug.localeCompare(b.region.slug);
    } else {
      return a.slug.localeCompare(b.slug);
    }
  } else if (aIsActive) {
    return -1;
  } else {
    return 1;
  }
}

type TCharacterLinkInfoProps = {
  character: TClaimedCharacter;
};

const CharacterLinkInfo: FC<TCharacterLinkInfoProps> = (props: TCharacterLinkInfoProps) => {
  const { character } = props;
  return (
    <div className="slds-grid slds-grid--align-spread slds-grid--vertical-align-center">
      <div className="slds-col slds-grow slds-text-align--left">
        <span className="slds-avatar slds-avatar--small slds-m-right--x-small rio-border--light rio-shadow--small">
          <SpecIcon class={character.details.class.slug} spec={character.details.spec.slug} />
        </span>
        <span className={`class-color--${character.details.class.id} rio-horiz-bar--container`}>{character.name}</span>
      </div>
      <div className="slds-col slds-text-align--right text-muted slds-text-body--small">
        {character.realm.name} ({character.region.short_name})
      </div>
    </div>
  );
};

type TLargeMainCharacterOptionProps = {
  mainCharacter: any;
  altCharacters: any[];
  favoriteCharacters: any[];
  otherCharacters: any[];
  teams: any[];
  siteConfig: any;
};

const makeLargeMainCharacterOption = (props: TLargeMainCharacterOptionProps) => {
  const { mainCharacter, altCharacters, favoriteCharacters, otherCharacters, teams } = props;

  if (!mainCharacter || !mainCharacter.details) {
    return null;
  }

  const teamsByType = teams.reduce(
    (accum: any, team: any) => {
      if (team.group_type === GroupTypes.MYTHICPLUS) {
        accum.mythicPlusTeams.push(team);
      } else if (team.group_type === GroupTypes.EVENT) {
        accum.eventTeams.push(team);
      } else {
        // ignore unknown type
      }
      return accum;
    },
    {
      eventTeams: [],
      mythicPlusTeams: []
    }
  );

  return (
    <SubMenu
      className="rio-auth-nav__button"
      key={mainCharacter.details.path}
      onTitleClick={handleRCMenuOnClick}
      title={
        <span>
          <span className="slds-avatar slds-avatar--x-small slds-m-right--x-small rio-border--light rio-shadow--small">
            <SpecIcon size={18} class={mainCharacter.details.class.slug} spec={mainCharacter.details.spec.slug} />
          </span>
          <span className={`class-color--${mainCharacter.details.class.id}`}>{mainCharacter.name}</span>
        </span>
      }
    >
      {favoriteCharacters.length > 1 && (
        <SubMenu
          title={
            <span>
              <i className="fa fa-fw fa-star" /> &nbsp; {t('nav:auth.dropdowns.favorites')}
            </span>
          }
        >
          {favoriteCharacters.map((ch: any) => (
            <MenuItem key={ch.details.path}>
              <CharacterLinkInfo character={ch} />
            </MenuItem>
          ))}
        </SubMenu>
      )}

      {altCharacters.length > 0 && (
        <SubMenu
          title={
            <span>
              <i className="fa fa-fw fa-user-alt" /> &nbsp; {t('nav:auth.dropdowns.max-level-alts')}
            </span>
          }
        >
          {altCharacters.map((ch: any) => (
            <MenuItem key={ch.details.path}>
              <CharacterLinkInfo character={ch} />
            </MenuItem>
          ))}
        </SubMenu>
      )}

      {otherCharacters.length > 0 && (
        <SubMenu
          title={
            <span>
              <i className="fa fa-fw fa-user-group" /> &nbsp; {t('nav:auth.dropdowns.other-characters')}
            </span>
          }
        >
          {otherCharacters.map((ch: any) => (
            <MenuItem key={ch.details.path}>
              <CharacterLinkInfo character={ch} />
            </MenuItem>
          ))}
        </SubMenu>
      )}

      <Divider />

      {mainCharacter && mainCharacter.details && mainCharacter.details.guild && (
        <MenuItem
          key={resolveRoutePath(namedRoutes.GuildDetails, {
            region: mainCharacter.details.guild.region.slug,
            realm: mainCharacter.details.guild.realm.slug,
            guild: mainCharacter.details.guild.name
          })}
        >
          <i className="fa fa-fw fa-users-class" /> &nbsp;{' '}
          <span className={`rio-faction-color--${mainCharacter.details.guild.faction}`}>
            &lt;
            {mainCharacter.details.guild.name}
            &gt;
          </span>
        </MenuItem>
      )}

      {teamsByType.mythicPlusTeams.length > 1 && (
        <SubMenu
          title={
            <span>
              <i className="fa fa-fw fa-user-friends" /> &nbsp; {t('nav:auth.dropdowns.mplus-teams')}
            </span>
          }
        >
          {teamsByType.mythicPlusTeams.map((team: any) => (
            <MenuItem
              key={resolveRoutePath(namedRoutes.TeamDetails, {
                team: team.slug,
                region: team.region.slug
              })}
            >
              <span className={`rio-faction-color--${team.faction}`}>{team.name}</span>
              {team.group_type !== GroupTypes.EVENT && <> ({team.region.short_name})</>}
            </MenuItem>
          ))}
        </SubMenu>
      )}
      {teamsByType.mythicPlusTeams.length === 1 && (
        <MenuItem
          key={resolveRoutePath(namedRoutes.TeamDetails, {
            team: teamsByType.mythicPlusTeams[0].slug,
            region: teamsByType.mythicPlusTeams[0].region.slug
          })}
        >
          <i className="fa fa-fw fa-user-friends" /> &nbsp;{' '}
          <span className={`rio-faction-color--${teamsByType.mythicPlusTeams[0].faction}`}>
            {teamsByType.mythicPlusTeams[0].name}
          </span>
          {teamsByType.mythicPlusTeams[0].group_type !== GroupTypes.EVENT && (
            <> ({teamsByType.mythicPlusTeams[0].region.short_name})</>
          )}
        </MenuItem>
      )}

      {teamsByType.eventTeams.length > 0 && (
        <SubMenu
          title={
            <span>
              <i className="fa fa-fw fa-trophy-star" /> &nbsp; {t('nav:auth.dropdowns.event-teams')}
            </span>
          }
        >
          {teamsByType.eventTeams.map((team: any) => (
            <MenuItem
              key={resolveRoutePath(namedRoutes.EventTeamDetails, {
                namespace: team.eventsData?.namespace,
                team: team.slug,
                region: team.region.slug
              })}
            >
              <span className={`rio-faction-color--${team.faction}`}>{team.name}</span>
            </MenuItem>
          ))}
        </SubMenu>
      )}

      {(teamsByType.mythicPlusTeams.length > 0 ||
        teamsByType.eventTeams.length > 0 ||
        (mainCharacter && mainCharacter.details && mainCharacter.details.guild)) && <Divider />}

      <MenuItem
        key={resolveRoutePath(namedRoutes.RaidRealmRankings, {
          raid: props.siteConfig.currentRaidSlug,
          difficulty: props.siteConfig.currentRaidDifficulty, // TODO: pick right difficulty
          region: mainCharacter.region.slug,
          realm: mainCharacter.realm.isConnected ? `connected-${mainCharacter.realm.slug}` : mainCharacter.realm.slug
        })}
      >
        <i className="fa fa-fw fa-ballot" /> &nbsp; {t('nav:auth.dropdowns.raid-rankings')}
      </MenuItem>
      <MenuItem
        key={resolveRoutePath(namedRoutes.KeystoneCharacterRealmRankings, {
          season: props.siteConfig.currentSeasonSlug,
          region: mainCharacter.region.slug,
          realm: mainCharacter.realm.slug,
          class: 'all',
          role: 'all'
        })}
      >
        <i className="fa fa-fw fa-user-chart" /> &nbsp; {t('nav:auth.dropdowns.mplus-character-rankings')}
      </MenuItem>
    </SubMenu>
  );
};

export const LargeAuthBarUnauthenticatedColumns: FC<any> = (props) => {
  return (
    <>
      <div className="slds-col slds-grow-none slds-p-right--xx-small">
        <NotificationBell />
      </div>
      <div className="slds-col slds-p-right--x-small">
        <MainNavLoginButton />
      </div>
      <div className="slds-col slds-p-right--x-small">
        <MainNavSignupButton />
      </div>
    </>
  );
};

export const LargeAuthBarAuthenticatedColumns = withSiteMeta((props: any) => {
  let mainCharacter: any = null;
  if (props.auth.claimedCharacters.length > 0) {
    mainCharacter = props.auth.claimedCharacters.find((ch: any) => ch.is_main_character);
  }

  const activeCharacters = props.auth.claimedCharacters.filter((ch: any) => ch.details);

  const altCharacters = activeCharacters.filter(
    (ch: any) => ch !== mainCharacter && ch.details.level >= LevelCap.Current
  );

  const otherCharacters = activeCharacters.filter(
    (ch: any) => ch !== mainCharacter && ch.details.level < LevelCap.Previous
  );

  const favoriteCharacters = activeCharacters.filter((ch: any) => ch.favorite);

  const teams = props.auth.memberGroupCharters?.map((gc: any) => gc.groupCharter).sort(sortTeams) || [];

  const adminOptions: any[] = [];

  if (props.currentFeatures[FeatureFlag.EVENT_CREATION_SYSTEM]) {
    adminOptions.push(
      <MenuItem key={namedRoutes.EventsManagement}>
        <i className="fa fa-fw fa-calendar" /> &nbsp; {t('nav:auth.dropdowns.events-management')}
      </MenuItem>
    );
  }

  if (props.currentFeatures[FeatureFlag.TIMELINE_EVENT_MANAGER_VIEW]) {
    adminOptions.push(
      <MenuItem key={namedRoutes.TimelineEventManagement}>
        <i className="fa fa-fw fa-timeline" /> &nbsp; {t('nav:auth.dropdowns.timeline-event-manager')}
      </MenuItem>
    );
  }

  if (props.currentFeatures[FeatureFlag.FILE_MANAGER_VIEW]) {
    adminOptions.push(
      <MenuItem key={'/admin/file-manager'}>
        <i className="fa fa-fw fa-folder-arrow-up" /> &nbsp; {t('nav:auth.dropdowns.file-manager')}
      </MenuItem>
    );
  }

  if (props.currentFeatures[FeatureFlag.CONTENT_MODERATOR] || props.currentFeatures[FeatureFlag.ADMIN]) {
    adminOptions.push(
      <MenuItem key={namedRoutes.SiteModeration}>
        <i className="fa fa-fw fa-shield-halved" /> &nbsp; {t('nav:auth.dropdowns.site-moderation')}
      </MenuItem>
    );
  }

  if (isTalentFeedbackAdminEnabled(props.currentFeatures)) {
    adminOptions.push(
      <MenuItem key={namedRoutes.TalentSemanticsAdmin}>
        <i className="fa fa-fw fa-flag" /> &nbsp; {t('nav:auth.dropdowns.talent-feedback')}
      </MenuItem>
    );
  }

  if (
    props.currentFeatures.admin ||
    props.currentFeatures.news_editor ||
    props.currentFeatures.news_admin ||
    props.currentFeatures.threads_editor ||
    props.currentFeatures.threads_admin ||
    props.siteConfig.isImpersonating
  ) {
    adminOptions.push(
      <MenuItem key="/admin">
        <i className="fa fa-fw fa-toolbox" /> &nbsp; {t('nav:auth.dropdowns.dashboard')}
      </MenuItem>
    );

    if (props.siteConfig.isImpersonating) {
      adminOptions.push(
        <MenuItem key="/stop-impersonate">
          <i className="fa fa-fw fa-user-slash" /> &nbsp;{' '}
          <b className="text-raiderio"> {t('nav:auth.dropdowns.stop-impersonating')} &nbsp;</b>
        </MenuItem>
      );
    }

    if (adminOptions.length > 0) {
      adminOptions.unshift(<Divider key="div" />);
    }
  }

  return (
    <>
      <div className="slds-col slds-grow-none slds-p-left--xx-small">
        <NotificationBell />
      </div>
      <div className="slds-col slds-text-align--right">
        <Menu
          onClick={handleRCMenuOnClick}
          selectable={false}
          mode="horizontal"
          disabledOverflow={true}
          className="no-background"
        >
          {activeCharacters.length > 0 && mainCharacter && !props.auth.avatar && (
            <MenuItem className="text-raiderio" key={'/settings/profile'}>
              {t('nav:auth.desktop.choose-avatar-prompt')}
            </MenuItem>
          )}

          {/* This has to be a function call rather than React component due to how rc-menu works */}
          {makeLargeMainCharacterOption({
            mainCharacter: mainCharacter,
            altCharacters: altCharacters,
            favoriteCharacters: favoriteCharacters,
            otherCharacters: otherCharacters,
            teams: teams,
            siteConfig: props.siteConfig
          })}

          {props.auth.claimedCharacters.length === 0 && (
            <MenuItem className="text-raiderio" key="/settings/battlenet">
              {t('nav:auth.desktop.claim-characters-prompt')}
            </MenuItem>
          )}

          {activeCharacters.length > 0 && !mainCharacter && (
            <MenuItem className="text-raiderio" key={`/settings/characters/${activeCharacters[0].details.id}/profile`}>
              {t('nav:auth.desktop.choose-main-character-prompt')}
            </MenuItem>
          )}

          {props.currentFeatures.message_center && (
            <MenuItem key={resolveRoutePath(namedRoutes.MessageCenterHomePage)}>
              <MessageCenterIcon size="xx-small" />
            </MenuItem>
          )}
        </Menu>
      </div>
      <div className="slds-col slds-grow-none slds-p-left--xx-small">
        <Menu
          onClick={handleRCMenuOnClick}
          selectable={false}
          mode="horizontal"
          disabledOverflow={true}
          className="no-background"
        >
          <SubMenu
            key={resolveRoutePath(namedRoutes.UserProfile, { name: props.auth.name })}
            onTitleClick={handleRCMenuOnClick}
            className="rio-menu--small-buttons"
            title={
              props.avatar ? (
                <UserAvatarIcon icon={props.avatar} size="x-small" />
              ) : (
                <i className="fas fa-user-circle" />
              )
            }
          >
            <MenuItem key={resolveRoutePath(namedRoutes.UserProfile, { name: props.auth.name })}>
              <i className="fa fa-fw fa-user" /> &nbsp; {props.auth.name}
            </MenuItem>
            <Divider />
            <MenuItem key="/settings">
              <i className="fa fa-fw fa-cog" /> &nbsp; {t('nav:auth.dropdowns.settings')}
            </MenuItem>
            {props.currentFeatures.recruitment_view && (
              <MenuItem key="/settings/recruitment/profiles">
                <i className="fa fa-fw fa-people-arrows-left-right" /> &nbsp;{' '}
                {t('nav:auth.dropdowns.recruitment-profiles')}
              </MenuItem>
            )}
            {props.currentFeatures[FeatureFlag.DOWNLOAD_REPLAYS] && (
              <MenuItem key={namedRoutes.ReplaysSettings}>
                <i className="fa fa-fw fa-route" /> &nbsp; {t('nav:auth.dropdowns.mplus-replays')}
              </MenuItem>
            )}

            {adminOptions}

            <Divider />
            <MenuItem key="/logout">
              <i className="fa fa-fw fa-sign-out-alt" /> &nbsp; {t('nav:auth.logout')}
            </MenuItem>
          </SubMenu>
        </Menu>
      </div>
    </>
  );
});

export const SmallAuthBarUnauthenticatedColumns = () => {
  return (
    <div className="slds-grid slds-grid--align-end slds-grid--vertical-align-center rio-auth-nav">
      <NotificationBell className="slds-col slds-grow-none slds-m-right--xx-small" />
      <MainNavLoginButton className="slds-col slds-grow-none slds-m-right--xx-small" />
      <MainNavSignupButton className="slds-col slds-grow-none slds-m-right--xx-small" />
    </div>
  );
};

export const SmallAuthBarAuthenticatedColumns = withSiteMeta((props: any) => {
  let mainCharacter: any = null;
  if (props.auth.claimedCharacters.length > 0) {
    mainCharacter = props.auth.claimedCharacters.find((ch: any) => ch.is_main_character);
  }

  const activeCharacters = props.auth.claimedCharacters.filter((ch: any) => ch.details);

  return (
    <>
      <div className="rio-auth-nav slds-grid slds-grid--vertical-align-center slds-grid--align-end">
        <NotificationBell className="slds-m-right--xx-small" />
        <Menu
          onClick={handleRCMenuOnClick}
          selectable={false}
          mode="horizontal"
          disabledOverflow={true}
          className="no-background"
        >
          {activeCharacters.length > 0 && mainCharacter && !props.auth.avatar && (
            <MenuItem className="text-raiderio" key={'/settings/profile'}>
              {t('nav:auth.mobile.choose-avatar-prompt')}
            </MenuItem>
          )}

          {mainCharacter && mainCharacter.details && (
            <SubMenu
              title={
                <DropdownTitle
                  title={<span className={`class-color--${mainCharacter.details.class.id}`}>{mainCharacter.name}</span>}
                />
              }
            >
              <MenuItem key={resolveRoutePath(namedRoutes.UserProfile, { name: props.auth.name })}>
                <span>
                  <UserAvatarIcon icon={props.auth.avatar} size="x-small" /> &nbsp; {props.auth.name}
                </span>
              </MenuItem>

              {props.currentFeatures.message_center && (
                <MenuItem key={resolveRoutePath(namedRoutes.MessageCenterHomePage)}>
                  <span>
                    <MessageCenterIcon icon={props.auth.avatar} size="x-small" /> &nbsp;{' '}
                    {t('nav:auth.dropdowns.messages')}
                  </span>
                </MenuItem>
              )}
              <Divider />

              <MenuItem key={mainCharacter.details.path}>
                <span className={`class-color--${mainCharacter.details.class.id}`}>
                  {t('nav:auth.dropdowns.view-character')}
                </span>
              </MenuItem>

              {mainCharacter.details.guild && (
                <MenuItem
                  className={`rio-faction-color--${mainCharacter.details.guild.faction}`}
                  key={resolveRoutePath(namedRoutes.GuildDetails, {
                    region: mainCharacter.details.guild.region.slug,
                    realm: mainCharacter.details.guild.realm.slug,
                    guild: mainCharacter.details.guild.name
                  })}
                >
                  &lt;
                  {mainCharacter.details.guild.name}
                  &gt;
                </MenuItem>
              )}

              <MenuItem
                key={resolveRoutePath(namedRoutes.RaidRealmRankings, {
                  raid: props.siteConfig.currentRaidSlug,
                  difficulty: props.siteConfig.currentRaidDifficulty, // TODO: pick right difficulty
                  region: mainCharacter.region.slug,
                  realm: mainCharacter.realm.isConnected
                    ? `connected-${mainCharacter.realm.slug}`
                    : mainCharacter.realm.slug
                })}
              >
                {t('nav:auth.dropdowns.realm-raid-rankings')}
              </MenuItem>
              <MenuItem
                key={resolveRoutePath(namedRoutes.KeystoneCharacterRealmRankings, {
                  season: props.siteConfig.currentSeasonSlug,
                  region: mainCharacter.region.slug,
                  realm: mainCharacter.realm.slug,
                  class: 'all',
                  role: 'all'
                })}
              >
                {t('nav:auth.dropdowns.realm-mplus-rankings')}
              </MenuItem>
              <Divider />
              <MenuItem key="/settings">{t('nav:auth.dropdowns.settings')}</MenuItem>
              {props.currentFeatures[FeatureFlag.EVENT_CREATION_SYSTEM] && (
                <MenuItem key={namedRoutes.EventsManagement}>{t('nav:auth.dropdowns.events-management')}</MenuItem>
              )}
              {props.currentFeatures[FeatureFlag.TIMELINE_EVENT_MANAGER_VIEW] && (
                <MenuItem key={namedRoutes.TimelineEventManagement}>
                  {t('nav:auth.dropdowns.timeline-event-manager')}
                </MenuItem>
              )}
              {/* Separate conditionals (not a Fragment): rc-menu only parses MenuItem/SubMenu as direct children. */}
              {(props.currentFeatures[FeatureFlag.CONTENT_MODERATOR] || props.currentFeatures[FeatureFlag.ADMIN]) && (
                <MenuItem key={namedRoutes.SiteModeration}>{t('nav:auth.dropdowns.site-moderation')}</MenuItem>
              )}
              {isTalentFeedbackAdminEnabled(props.currentFeatures) && (
                <MenuItem key={namedRoutes.TalentSemanticsAdmin}>{t('nav:auth.dropdowns.talent-feedback')}</MenuItem>
              )}
              {props.auth.claimedCharacters && props.auth.claimedCharacters.length > 0 && (
                <SubMenu title={<span>Characters</span>}>
                  {activeCharacters.map((ch: any, index: number) => (
                    <MenuItem key={ch.details.path} className={`class-color--${ch.details.class.id}`}>
                      {ch.name}
                    </MenuItem>
                  ))}
                </SubMenu>
              )}
              <Divider />
              <MenuItem key="/logout">{t('nav:auth.logout')}</MenuItem>
            </SubMenu>
          )}

          {props.auth.claimedCharacters.length === 0 && (
            <MenuItem className="text-raiderio" key="/settings/battlenet">
              {t('nav:auth.mobile.claim-characters-prompt')}
            </MenuItem>
          )}

          {activeCharacters.length > 0 && !mainCharacter && (
            <MenuItem className="text-raiderio" key={`/settings/characters/${activeCharacters[0].details.id}/profile`}>
              {t('nav:auth.mobile.choose-main-character-prompt')}
            </MenuItem>
          )}
        </Menu>
      </div>
    </>
  );
});
